import {computed, ref, Ref, watch} from 'vue'
import type {DataColumn, DataError, DataProvider, DataRow} from '@hantera/design-system'
import type {ClientListItem, ClientsListParams} from '../../../backend/iam.types'
import type iam from '../../../backend/iam'

export class ClientsDataProvider implements DataProvider {
  private _rows = ref<DataRow[]>([])
  private _isLoading = ref(false)
  private _allLoaded = ref(false)
  private _totalCount = ref(0)
  private _entries: ClientListItem[] = []
  private _lastCursor: string | undefined = undefined
  private _errors = ref<DataError[]>([])

  public readonly rows = computed(() => this._rows.value)
  public readonly isLoading = computed(() => this._isLoading.value)
  public readonly allLoaded = computed(() => this._allLoaded.value)
  public readonly errors = computed(() => this._errors.value.length > 0 ? this._errors.value : undefined)

  public readonly columns = computed<DataColumn[]>(() => [
    {
      key: 'name',
      label: this.t('columns.name'),
      valueIdx: 0,
      canSort: true,
      canFilter: true,
      sort: this.filters.value.orderBy?.startsWith('name')
        ? (this.filters.value.orderBy.includes('desc') ? 'desc' : 'asc')
        : undefined,
      values: {
        type: 'text',
        isArray: false,
      },
    },
    {
      key: 'description',
      label: this.t('columns.description'),
      valueIdx: 1,
      canFilter: false,
      values: {
        type: 'text',
        isArray: false
      },
    },
  ])

  public readonly totalCount = computed(() => {
    return {
      count: this._totalCount.value,
    }
  })

  constructor(
    private filters: Ref<ClientsListParams>,
    private iamService: ReturnType<typeof iam>,
    private t: (key: string) => string,
  ) {
    // Watch filters and refresh when they change
    watch(
      filters,
      () => {
        this.refresh()
      },
      { deep: true },
    )
  }

  public async refresh(): Promise<void> {
    this._isLoading.value = true
    this._errors.value = []
    this._entries = []
    this._rows.value = []
    this._lastCursor = undefined
    this._allLoaded.value = false

    try {
      const response = await this.iamService.getClients({
        ...this.filters.value,
        after: undefined,
        before: undefined,
      })

      this._entries = response.clients
      this._rows.value = this._entries.map((c) => this.entryToRow(c))
      this._lastCursor = response.lastCursor
      this._totalCount.value = response.totalCount
      this._allLoaded.value = this._rows.value.length == response.totalCount
    } catch (error: any) {
      console.error('Failed to load clients:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || this.t('errors.failedToLoad'),
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public async loadMore(): Promise<void> {
    if (this._isLoading.value || this._allLoaded.value || !this._lastCursor) {
      return
    }

    this._isLoading.value = true
    this._errors.value = []

    try {
      const response = await this.iamService.getClients({
        ...this.filters.value,
        after: this._lastCursor,
        before: undefined,
      })

      this._entries.push(...response.clients)
      this._rows.value = this._entries.map((c) => this.entryToRow(c))
      this._lastCursor = response.lastCursor
      this._allLoaded.value = this._rows.value.length == response.totalCount
    } catch (error: any) {
      console.error('Failed to load more clients:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || this.t('errors.failedToLoadMore'),
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public setSort(column: DataColumn, direction?: 'asc' | 'desc'): void {
    if (column.key === 'name') {
      this.filters.value.orderBy = direction ? `${column.key}${direction === 'desc' ? ' desc' : ''}` : undefined
    }
  }

  private entryToRow(client: ClientListItem): DataRow {
    return {
      key: client.id,
      values: [
        client.name || client.id,
        client.description,
      ],
    }
  }
}
